import { Fragment, useEffect, useMemo, useState, type FormEvent } from "react";
import { supabase } from "../lib/supabase.js";
import { normalizeAuPhone } from "../lib/phone.js";
import { useAuth, useReadOnly } from "../lib/auth.js";
import { IntakeFieldInput } from "../lib/IntakeFieldInput.js";
import { emptyCustomFieldValues, type IntakeFieldDef } from "../lib/intakeFields.js";
import { Badge, Button, Card, EmptyState, Field, Input, Modal, Select } from "../lib/ui.js";
import { StaffAvailabilityGrid, type GridResource } from "../lib/StaffAvailabilityGrid.js";
import { bySurname } from "../lib/staffOrder.js";
import { BookingDetailsModal } from "./BookingDetails.js";
import { QuickActions, type QuickAction } from "../lib/QuickActions.js";
import { minToTimeLabel, todayStr, type AvailabilityExceptionRow, type AvailabilityWindowRow, type BusyItem } from "../lib/availability.js";
import { formatDateTimeMedium, formatTime, formatWeekday, formatWeekdayOnly } from "../lib/datetime.js";
import { addDaysYmd, endOfDayExclusive, startOfDayInZone, wallTimeOfMinutes, ymdOfInstant, zonedWallToInstant } from "../lib/zone.js";
import { StickyTitle } from "../lib/StickyTitle.js";
import { CompleteBookingModal, CompleteBookingsModal, bookingTotal, canMarkCompleted } from "../lib/CompleteBookingModal.js";
import { useLiveRefresh } from "../lib/useLiveRefresh.js";
import { groupByCategory } from "../book/helpers.js";

/** The tables behind the schedule grid; a change to any of them for this business re-reads the day. */
const LIVE_TABLES = ["booking_items", "bookings", "booking_holds"] as const;

interface BookingItemInfo {
  id: string;
  service_id: string;
  service_name: string | null;
  price_cents: number | null;
  starts_at: string;
  ends_at: string;
  custom_fields: Record<string, unknown> | null;
}

interface BookingRow {
  id: string;
  status: string;
  created_via: string;
  created_at: string;
  customer_name: string | null;
  /** The caller who made the booking, when the visit is for someone else (customer_name is then that person). */
  booked_by: string | null;
  /** The name the visit is under when it is for someone else; null = the account holder's own visit. */
  guest_name: string | null;
  customer_id: string | null;
  /** All items for this booking, in schedule order. A "haircut + colouring" booking has one
   *  bookings row and two of these, back to back on the same resource -- see the Multi-Service
   *  Booking Policy note on the create-flow below. */
  items: BookingItemInfo[];
  resource_id: string | null;
  resource_name: string | null;
}

interface CustomerOption {
  id: string;
  name: string | null;
}

interface ServiceOption {
  id: string;
  name: string;
  category: string | null;
  duration_minutes: number;
  intake_fields: IntakeFieldDef[];
}

interface ResourceOption {
  id: string;
  name: string;
  resource_type_id: string | null;
}

interface ServiceResourceRule {
  service_id: string;
  resource_type_id: string | null;
  resource_id: string | null;
}

const BOOKING_ROW_SELECT =
  "id, status, created_via, created_at, customer_id, guest_name, customers(name), booking_items(id, starts_at, ends_at, cancelled_at, custom_fields, service_id, resource_id, services(name, price_cents), resources(name))";

/** One `bookings` row (joined as BOOKING_ROW_SELECT) as the page shows it. */
function toBookingRow(b: any): BookingRow {
  // A confirmed booking shows only its live services (one of them may have been cancelled on its own);
  // a cancelled booking keeps showing what it had.
  const shown = (b.booking_items ?? []).filter((i: any) => b.status !== "confirmed" || !i.cancelled_at);
  const items: BookingItemInfo[] = shown
    .slice()
    .sort((x: any, y: any) => (x.starts_at < y.starts_at ? -1 : 1))
    .map((i: any) => ({
      id: i.id,
      service_id: i.service_id,
      service_name: i.services?.name ?? null,
      price_cents: i.services?.price_cents ?? null,
      starts_at: i.starts_at,
      ends_at: i.ends_at,
      custom_fields: i.custom_fields,
    }));
  return {
    id: b.id,
    status: b.status,
    created_via: b.created_via,
    created_at: b.created_at,
    customer_name: b.guest_name ?? b.customers?.name ?? null,
    booked_by: b.guest_name ? (b.customers?.name ?? null) : null,
    guest_name: b.guest_name ?? null,
    customer_id: b.customer_id ?? null,
    resource_id: shown[0]?.resource_id ?? null,
    resource_name: shown[0]?.resources?.name ?? null,
    items,
  };
}

const PAGE_SIZE = 15;
const STATUS_TONE: Record<string, "success" | "danger" | "neutral" | "warning"> = {
  confirmed: "success",
  cancelled: "danger",
  completed: "neutral",
  no_show: "warning",
};


/**
 * Two pages share this code: "New booking" (/app/new-booking, the day schedule with the booking form) and "Bookings"
 * (/app/bookings, the list of all bookings with their actions).
 */
export function Bookings({ view }: { view: "schedule" | "list" }) {
  const { activeBusinessId } = useAuth();
  const readOnly = useReadOnly();
  const [loading, setLoading] = useState(true);

  const [rows, setRows] = useState<BookingRow[]>([]);
  const [customers, setCustomers] = useState<CustomerOption[]>([]);
  const [services, setServices] = useState<ServiceOption[]>([]);
  const [resources, setResources] = useState<ResourceOption[]>([]);
  const [rules, setRules] = useState<ServiceResourceRule[]>([]);
  const [windows, setWindows] = useState<AvailabilityWindowRow[]>([]);
  const [exceptions, setExceptions] = useState<AvailabilityExceptionRow[]>([]);

  // ---- List view state ----
  const [search, setSearch] = useState("");
  // The list opens on confirmed bookings; pick another status (or "All statuses") to see the rest.
  const [statusFilter, setStatusFilter] = useState("confirmed");
  const [staffFilter, setStaffFilter] = useState("");
  const [page, setPage] = useState(0);
  const [completeTarget, setCompleteTarget] = useState<BookingRow | null>(null);
  const [completeMany, setCompleteMany] = useState<BookingRow[] | null>(null);
  /** A quick-action chosen on a row: Reschedule, Add service, Combine or Cancel (BookingActionHost does the rest). */
  const [bookingAction, setBookingAction] = useState<{ bookingId: string; action: BookingAction } | null>(null);
  const [detailsBookingId, setDetailsBookingId] = useState<string | null>(null);

  // ---- Schedule / create-booking state ----
  const [scheduleDate, setScheduleDate] = useState(todayStr());
  const [scheduleServiceIds, setScheduleServiceIds] = useState<string[]>([]);
  const [dayBusy, setDayBusy] = useState<BusyItem[]>([]);
  const [loadingDay, setLoadingDay] = useState(false);
  const [selectedSlot, setSelectedSlot] = useState<{ resourceId: string; startMin: number } | null>(null);
  const [customerMode, setCustomerMode] = useState<"existing" | "new">("existing");
  const [customerId, setCustomerId] = useState("");
  const [newCustomerName, setNewCustomerName] = useState("");
  const [newCustomerPhone, setNewCustomerPhone] = useState("");
  const [newCustomerDob, setNewCustomerDob] = useState("");
  const [businessState, setBusinessState] = useState<string | null>(null);
  const [customValues, setCustomValues] = useState<Record<string, unknown>>({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function loadStatic() {
    if (!activeBusinessId) return;
    setLoading(true);
    const exceptionsFrom = new Date(Date.now() - 7 * 86_400_000).toISOString();
    const [{ data: custs }, { data: svcs }, { data: res }, { data: srr }, { data: avail }, { data: exc }] = await Promise.all([
      supabase.from("customers").select("id, name").eq("business_id", activeBusinessId).order("name"),
      supabase
        .from("services")
        .select("id, name, category, duration_minutes, intake_fields")
        .eq("business_id", activeBusinessId)
        .eq("active", true)
        .order("name"),
      supabase.from("resources").select("id, name, resource_type_id").eq("business_id", activeBusinessId).eq("active", true).order("name"),
      supabase.from("service_resource_rules").select("service_id, resource_type_id, resource_id").eq("business_id", activeBusinessId),
      supabase.from("availability").select("resource_id, weekday, start_time, end_time").eq("business_id", activeBusinessId),
      supabase
        .from("availability_exceptions")
        .select("resource_id, starts_at, ends_at, kind")
        .eq("business_id", activeBusinessId)
        .gte("ends_at", exceptionsFrom),
    ]);
    const { data: business } = await supabase.from("businesses").select("state").eq("id", activeBusinessId).single();
    setBusinessState(business?.state ?? null);
    setCustomers((custs ?? []) as CustomerOption[]);
    setServices(
      groupByCategory((svcs ?? []) as ServiceOption[]).flatMap((g) => g.items), // grouped by category (uncategorised last), by name within
    );
    setResources((res ?? []) as ResourceOption[]);
    setRules((srr ?? []) as ServiceResourceRule[]);
    setWindows((avail ?? []) as AvailabilityWindowRow[]);
    setExceptions((exc ?? []) as AvailabilityExceptionRow[]);
    setLoading(false);
  }

  async function loadList() {
    if (!activeBusinessId) return;
    const { data: bookings } = await supabase
      .from("bookings")
      .select(BOOKING_ROW_SELECT)
      .eq("business_id", activeBusinessId)
      .order("created_at", { ascending: false })
      .limit(300);

    const list: BookingRow[] = (bookings ?? []).map(toBookingRow);
    setRows(list);
  }

  async function loadDaySchedule(date: string, quiet = false) {
    if (!activeBusinessId) return;
    if (!quiet) setLoadingDay(true);
    const dayStart = startOfDayInZone(date).toISOString();
    const dayEnd = new Date(endOfDayExclusive(date).getTime() - 1).toISOString();
    const [{ data: items }, { data: holds }] = await Promise.all([
      supabase
        .from("booking_items")
        .select("booking_id, resource_id, starts_at, ends_at, cancelled_at, services(name), bookings!inner(status, guest_name, customers(name))")
        .eq("business_id", activeBusinessId)
        .is("cancelled_at", null)
        .lt("starts_at", dayEnd)
        .gt("ends_at", dayStart),
      supabase
        .from("booking_holds")
        .select("resource_id, starts_at, ends_at, expires_at")
        .eq("business_id", activeBusinessId)
        .gt("expires_at", new Date().toISOString())
        .lt("starts_at", dayEnd)
        .gt("ends_at", dayStart),
    ]);

    const busy: BusyItem[] = [
      ...(items ?? [])
        .filter((i: any) => i.bookings?.status === "confirmed")
        .map((i: any) => ({
          resource_id: i.resource_id,
          starts_at: i.starts_at,
          ends_at: i.ends_at,
          kind: "booking" as const,
          label: `${i.bookings?.guest_name ?? i.bookings?.customers?.name ?? "Customer"} · ${i.services?.name ?? "Booking"}`,
          bookingId: i.booking_id as string,
        })),
      ...(holds ?? []).map((h: any) => ({
        resource_id: h.resource_id,
        starts_at: h.starts_at,
        ends_at: h.ends_at,
        kind: "hold" as const,
        label: "Held",
      })),
    ];
    setDayBusy(busy);
    setLoadingDay(false);
  }

  useEffect(() => {
    void loadStatic();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeBusinessId]);

  useEffect(() => {
    if (view === "list") void loadList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeBusinessId, view]);

  useEffect(() => {
    void loadDaySchedule(scheduleDate);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeBusinessId, scheduleDate]);

  // A phone booking or hold (or another staff member's change) shows up on the grid without a reload.
  useLiveRefresh(activeBusinessId, LIVE_TABLES, () => void loadDaySchedule(scheduleDate, true));

  const selectedServices = useMemo(
    () => scheduleServiceIds.map((id) => services.find((s) => s.id === id)).filter((s): s is ServiceOption => !!s),
    [services, scheduleServiceIds]
  );
  const totalDurationMin = useMemo(() => selectedServices.reduce((sum, s) => sum + s.duration_minutes, 0), [selectedServices]);

  /** Union of every selected service's intake fields, deduped by key -- one combined details
   *  form for the whole multi-service appointment rather than repeating shared fields (see
   *  Multi-Service Booking Policy). */
  const combinedIntakeFields = useMemo(() => {
    const seen = new Map<string, IntakeFieldDef>();
    for (const s of selectedServices) for (const f of s.intake_fields) if (!seen.has(f.key)) seen.set(f.key, f);
    return [...seen.values()];
  }, [selectedServices]);

  const scheduleResources: GridResource[] = useMemo(() => eligibleForAll(scheduleServiceIds, resources, rules).sort(bySurname), [scheduleServiceIds, resources, rules]);

  function toggleScheduleService(id: string) {
    setScheduleServiceIds((prev) => {
      const next = prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id];
      const nextServices = next.map((sid) => services.find((s) => s.id === sid)).filter((s): s is ServiceOption => !!s);
      const fields = new Map<string, IntakeFieldDef>();
      for (const s of nextServices) for (const f of s.intake_fields) if (!fields.has(f.key)) fields.set(f.key, f);
      setCustomValues((prevValues) => {
        const merged = emptyCustomFieldValues([...fields.values()]);
        for (const key of Object.keys(merged)) if (key in prevValues) merged[key] = prevValues[key];
        return merged;
      });
      return next;
    });
    setSelectedSlot(null);
  }

  function resetCreatePanel() {
    setCustomerMode("existing");
    setCustomerId("");
    setNewCustomerName("");
    setNewCustomerPhone("");
    setNewCustomerDob("");
    setScheduleServiceIds([]);
    setSelectedSlot(null);
    setCustomValues({});
    setError(null);
  }

  async function handleCreateBooking(e: FormEvent) {
    e.preventDefault();
    if (readOnly) return;
    if (!activeBusinessId || selectedServices.length === 0 || !selectedSlot) return;
    setError(null);
    setSaving(true);

    let finalCustomerId = customerId;
    if (customerMode === "new") {
      if (!newCustomerName) {
        setError("Customer name is required.");
        setSaving(false);
        return;
      }
      // Store the phone as E.164 so Caller ID matches it; reject a bad number before creating anything.
      let newCustomerE164: string | null = null;
      if (newCustomerPhone.trim()) {
        const normalized = normalizeAuPhone(newCustomerPhone, businessState);
        if (!normalized.ok) {
          setError(normalized.error);
          setSaving(false);
          return;
        }
        newCustomerE164 = normalized.e164;
      }
      const { data: customer, error: custErr } = await supabase
        .from("customers")
        .insert({ business_id: activeBusinessId, name: newCustomerName, date_of_birth: newCustomerDob || null })
        .select()
        .single();
      if (custErr || !customer) {
        setError(custErr?.message ?? "Could not create customer.");
        setSaving(false);
        return;
      }
      finalCustomerId = customer.id;
      if (newCustomerE164) {
        await supabase.from("customer_phones").insert({ business_id: activeBusinessId, customer_id: customer.id, e164: newCustomerE164 });
      }
    }
    if (!finalCustomerId) {
      setError("Select or create a customer.");
      setSaving(false);
      return;
    }

    // The slot is a time of day at the business: turned into the right instant in the business's zone, whatever the browser's.
    const blockStart = zonedWallToInstant(scheduleDate, wallTimeOfMinutes(selectedSlot.startMin));

    // One database function makes the whole booking (one booking, one item per service back to
    // back) and applies the same rules as a phone booking: staff must do every service, be open
    // for the whole visit, and the time must be free -- all or nothing.
    const { error: bookingErr } = await supabase.rpc("rpc_portal_create_booking", {
      p_business_id: activeBusinessId,
      p_customer_id: finalCustomerId,
      p_service_ids: scheduleServiceIds,
      p_resource_id: selectedSlot.resourceId,
      p_starts_at: blockStart.toISOString(),
      p_custom_fields: customValues,
    });
    if (bookingErr) {
      setError(bookingErrorMessage(bookingErr));
      setSaving(false);
      await loadDaySchedule(scheduleDate);
      return;
    }

    setSaving(false);
    resetCreatePanel();
    await Promise.all([loadDaySchedule(scheduleDate), view === "list" ? loadList() : Promise.resolve()]);
  }

  if (loading) return <p style={{ color: "var(--rc-text-muted)" }}>Loading...</p>;

  return (
    <div>
      <StickyTitle style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 700, margin: 0, color: "var(--rc-text)" }}>{view === "schedule" ? "New booking" : "Bookings"}</h1>
          <p style={{ fontSize: 13, color: "var(--rc-text-muted)", margin: "4px 0 0" }}>
            {view === "schedule" ? "Book an appointment for a customer, by the day." : "Manage existing appointments."}
          </p>
        </div>
      </StickyTitle>

      {view === "schedule" ? (
        <div className="rc-row">
          <Card className="rc-side" style={{ width: 320, flexShrink: 0 }}>
            <form onSubmit={handleCreateBooking} style={{ display: "flex", flexDirection: "column", gap: 14 }}>
              <div>
                <h2 style={{ fontSize: 14, fontWeight: 700, margin: "0 0 2px", color: "var(--rc-text)" }}>New booking</h2>
                <p style={{ fontSize: 12, color: "var(--rc-text-faint)", margin: 0 }}>
                  {readOnly ? "This login can look at the schedule but can't make bookings." : "Pick a service, then click an open slot on the schedule."}
                </p>
              </div>

              <Field label="Service(s)">
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 6,
                    maxHeight: 240,
                    overflowY: "auto",
                    border: "1px solid var(--rc-border)",
                    borderRadius: "var(--rc-radius-md)",
                    padding: 8,
                  }}
                >
                  {groupByCategory(services).map((g) => (
                    <Fragment key={g.title ?? "_"}>
                      {g.title && <div style={{ fontSize: 12, fontWeight: 700, color: "var(--rc-link)", marginTop: 2 }}>{g.title}</div>}
                      {g.items.map((s) => (
                        <label key={s.id} style={{ display: "flex", alignItems: "center", gap: 7, fontSize: 12.5, color: "var(--rc-text)" }}>
                          <input type="checkbox" checked={scheduleServiceIds.includes(s.id)} onChange={() => toggleScheduleService(s.id)} />
                          {s.name} ({s.duration_minutes} min)
                        </label>
                      ))}
                    </Fragment>
                  ))}
                  {services.length === 0 && <span style={{ fontSize: 12.5, color: "var(--rc-text-faint)" }}>No services yet.</span>}
                </div>
                {selectedServices.length > 1 && (
                  <p style={{ fontSize: 12, color: "var(--rc-text-faint)", margin: "4px 0 0" }}>
                    {selectedServices.length} services, {totalDurationMin} min total, back-to-back with the same staff member.
                  </p>
                )}
              </Field>

              <div
                style={{
                  fontSize: 12.5,
                  padding: "10px 12px",
                  borderRadius: "var(--rc-radius-md)",
                  background: selectedSlot ? "var(--rc-success-soft)" : "var(--rc-neutral-soft)",
                  color: selectedSlot ? "var(--rc-success)" : "var(--rc-text-faint)",
                }}
              >
                {selectedSlot
                  ? `Selected: ${resources.find((r) => r.id === selectedSlot.resourceId)?.name} at ${minToTimeLabel(selectedSlot.startMin)}`
                  : selectedServices.length > 0
                    ? "No time selected yet -- click an open slot on the schedule."
                    : "Pick a service to see availability."}
              </div>

              <fieldset style={{ border: "1px solid var(--rc-border)", borderRadius: "var(--rc-radius-md)", padding: 12, margin: 0 }}>
                <legend style={{ fontSize: 12, padding: "0 4px", color: "var(--rc-text-muted)", fontWeight: 600 }}>Customer</legend>
                <div style={{ display: "flex", gap: 14, marginBottom: 8, fontSize: 12.5 }}>
                  <label style={{ display: "flex", alignItems: "center", gap: 5 }}>
                    <input type="radio" checked={customerMode === "existing"} onChange={() => setCustomerMode("existing")} /> Existing
                  </label>
                  <label style={{ display: "flex", alignItems: "center", gap: 5 }}>
                    <input type="radio" checked={customerMode === "new"} onChange={() => setCustomerMode("new")} /> New
                  </label>
                </div>
                {customerMode === "existing" ? (
                  <Select value={customerId} onChange={(e) => setCustomerId(e.target.value)}>
                    <option value="">Select customer...</option>
                    {customers.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.name ?? "(no name)"}
                      </option>
                    ))}
                  </Select>
                ) : (
                  <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                    <Input placeholder="Name" value={newCustomerName} onChange={(e) => setNewCustomerName(e.target.value)} />
                    <Input placeholder="Phone" value={newCustomerPhone} onChange={(e) => setNewCustomerPhone(e.target.value)} />
                    <label style={{ fontSize: 12, color: "var(--rc-text-muted)", display: "flex", flexDirection: "column", gap: 3 }}>
                      Date of birth (optional)
                      <Input type="date" value={newCustomerDob} min="1900-01-01" max={todayStr()} onChange={(e) => setNewCustomerDob(e.target.value)} />
                    </label>
                  </div>
                )}
              </fieldset>

              {combinedIntakeFields.length > 0 && (
                <fieldset style={{ border: "1px solid var(--rc-border)", borderRadius: "var(--rc-radius-md)", padding: 12, margin: 0, display: "flex", flexDirection: "column", gap: 10 }}>
                  <legend style={{ fontSize: 12, padding: "0 4px", color: "var(--rc-text-muted)", fontWeight: 600 }}>Details</legend>
                  {combinedIntakeFields.map((f) => (
                    <IntakeFieldInput key={f.key} field={f} value={customValues[f.key]} onChange={(next) => setCustomValues((prev) => ({ ...prev, [f.key]: next }))} />
                  ))}
                </fieldset>
              )}

              {error && <p style={{ color: "var(--rc-danger)", fontSize: 12.5, margin: 0 }}>{error}</p>}

              <Button type="submit" variant="primary" disabled={readOnly || saving || !selectedSlot || selectedServices.length === 0}>
                {saving ? "Booking..." : "Confirm booking"}
              </Button>
            </form>
          </Card>

          <div className="rc-grow" style={{ flex: 1, minWidth: 0 }}>
            <Card padded={false} style={{ marginBottom: 12, padding: "10px 14px" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                <Button variant="ghost" onClick={() => setScheduleDate(shiftDate(scheduleDate, -1))}>
                  ← Prev
                </Button>
                <Input type="date" value={scheduleDate} onChange={(e) => setScheduleDate(e.target.value)} style={{ width: 160 }} />
                <Button variant="ghost" onClick={() => setScheduleDate(shiftDate(scheduleDate, 1))}>
                  Next →
                </Button>
                <Button variant="ghost" onClick={() => setScheduleDate(todayStr())}>
                  Today
                </Button>
                {scheduleDate && <span style={{ fontSize: 14, fontWeight: 700, color: "var(--rc-text)" }}>{weekdayName(scheduleDate)}</span>}
                <div style={{ flex: 1 }} />
                {loadingDay && <span style={{ fontSize: 12, color: "var(--rc-text-faint)" }}>Refreshing…</span>}
                <LegendDot color="var(--rc-primary)" label="Booked" />
                <LegendDot color="var(--rc-warning-soft)" border="var(--rc-warning)" label="Held" />
                <LegendDot hatched label="Closed" />
              </div>
            </Card>
            <StaffAvailabilityGrid
              date={scheduleDate}
              resources={scheduleResources}
              windows={windows}
              exceptions={exceptions}
              busy={dayBusy}
              slotDurationMin={totalDurationMin > 0 ? totalDurationMin : null}
              selected={selectedSlot}
              onSelectSlot={(resourceId, startMin) => !readOnly && setSelectedSlot({ resourceId, startMin })}
              onSelectBooking={(id) => setDetailsBookingId(id)}
            />
          </div>
        </div>
      ) : (
        <ListView
          rows={rows}
          resources={resources}
          search={search}
          setSearch={setSearch}
          statusFilter={statusFilter}
          setStatusFilter={setStatusFilter}
          staffFilter={staffFilter}
          setStaffFilter={setStaffFilter}
          page={page}
          setPage={setPage}
          onComplete={(r) => setCompleteTarget(r)}
          onCompleteMany={(rs) => setCompleteMany(rs)}
          onAction={(r, action) => setBookingAction({ bookingId: r.id, action })}
          onView={(r) => setDetailsBookingId(r.id)}
        />
      )}

      {completeTarget && (
        <CompleteBookingModal
          bookingId={completeTarget.id}
          customer={completeTarget.customer_name}
          service={bookingServiceLabel(completeTarget)}
          startsAt={bookingStartsAt(completeTarget)}
          {...bookingTotal(completeTarget.items.map((i) => i.price_cents))}
          onClose={() => setCompleteTarget(null)}
          onDone={() => Promise.all([loadList(), loadDaySchedule(scheduleDate)]).then(() => undefined)}
        />
      )}

      {completeMany && (
        <CompleteBookingsModal
          items={completeMany.map((r) => ({ id: r.id, customer: r.customer_name, service: bookingServiceLabel(r), startsAt: bookingStartsAt(r), ...bookingTotal(r.items.map((i) => i.price_cents)) }))}
          onClose={() => setCompleteMany(null)}
          onDone={() => Promise.all([loadList(), loadDaySchedule(scheduleDate)]).then(() => undefined)}
        />
      )}

      {detailsBookingId && <BookingDetailsModal bookingId={detailsBookingId} onClose={() => setDetailsBookingId(null)} />}

      {bookingAction && activeBusinessId && (
        <BookingActionHost
          businessId={activeBusinessId}
          bookingId={bookingAction.bookingId}
          action={bookingAction.action}
          onClose={() => setBookingAction(null)}
          onChanged={() => Promise.all([loadList(), loadDaySchedule(scheduleDate)]).then(() => undefined)}
        />
      )}
    </div>
  );
}

/** Multi-Service Booking Policy: a booking with multiple services (e.g. "haircut + colouring")
 *  is one `bookings` row with multiple `booking_items`, scheduled back to back on the same
 *  resource (created and moved by the shared database functions, the same ones the phone uses).
 *  These two helpers read that back out for display; module-scope (not a Bookings() component method) so
 *  ListView and RescheduleModal can use them too. */
function bookingServiceLabel(row: BookingRow): string {
  return row.items.length ? row.items.map((i) => i.service_name ?? "Service").join(" + ") : "-";
}
function bookingStartsAt(row: BookingRow): string | null {
  return row.items[0]?.starts_at ?? null;
}

/** Resources eligible for EVERY service in `serviceIds` (the same staff member must be able to
 *  perform the whole sequence) -- the intersection of each service's own eligible set. Same rule
 *  as the database (and the phone): a service with no rule names nobody, so nobody is eligible. */
function eligibleForAll(serviceIds: string[], resources: ResourceOption[], rules: ServiceResourceRule[]): ResourceOption[] {
  return resources.filter((r) =>
    serviceIds.every((sid) => {
      const svcRules = rules.filter((rule) => rule.service_id === sid);
      return svcRules.some((rule) => rule.resource_id === r.id || (rule.resource_type_id && rule.resource_type_id === r.resource_type_id));
    })
  );
}

/** Plain-language text for the booking functions' errors (the same ones the phone path reports). */
function bookingErrorMessage(err: { message?: string; code?: string }): string {
  const m = err.message ?? "";
  if (err.code === "23P01" || m.includes("slot_unavailable")) return "That time is no longer free -- pick another time.";
  if (m.includes("time_in_past")) return "That time has already passed -- pick a time from now on.";
  if (m.includes("outside_business_hours")) return "That is outside this staff member's working hours, or they are on time off -- pick another time.";
  if (m.includes("service_not_offered_by_resource")) return "That staff member isn't set up to do all of these services (see the Services page).";
  if (m.includes("bookings_different_staff")) return "Those bookings are with different staff members, so they can't be joined into one visit.";
  if (m.includes("bookings_not_combinable")) return "Those bookings can't be joined into one visit.";
  if (m.includes("not_authorized")) return "You don't have access to change that booking.";
  return m || "That couldn't be completed.";
}

/** "Wednesday" for a Date or an instant (shown at the business) or a YYYY-MM-DD calendar date (shown as that day, in any zone). */
function weekdayName(d: Date | string): string {
  return typeof d === "string" && /^\d{4}-\d{2}-\d{2}$/.test(d) ? formatWeekdayOnly(d) : formatWeekday(d);
}

/** "Wednesday, 7 Oct 2026, 9:15 am" at the business. */
function formatWhen(iso: string): string {
  return `${weekdayName(iso)}, ${formatDateTimeMedium(iso)}`;
}

/** A calendar date shifted by whole days (zone-free). */
function shiftDate(dateStr: string, days: number): string {
  return addDaysYmd(dateStr, days);
}

function LegendDot({ color, border, hatched, label }: { color?: string; border?: string; hatched?: boolean; label: string }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 11.5, color: "var(--rc-text-faint)" }}>
      <span
        style={{
          width: 10,
          height: 10,
          borderRadius: 2,
          background: hatched ? "repeating-linear-gradient(135deg, var(--rc-neutral-soft), var(--rc-neutral-soft) 2px, var(--rc-surface-sunken) 2px, var(--rc-surface-sunken) 4px)" : color,
          border: border ? `1.5px dashed ${border}` : "1px solid var(--rc-border)",
        }}
      />
      {label}
    </div>
  );
}

/** What a booking's quick-action menu offers. One wording for the Bookings list and the Dashboard: View always; the four changes
 *  only while the booking is confirmed. */
export type BookingAction = "reschedule" | "addService" | "combine" | "cancel";
export function bookingQuickActions(status: string, on: { view: () => void; act: (action: BookingAction) => void; canCombine: boolean; readOnly?: boolean }): QuickAction[] {
  return [
    { label: "View", onSelect: on.view },
    ...(status === "confirmed" && !on.readOnly
      ? [
          { label: "Reschedule", onSelect: () => on.act("reschedule") },
          { label: "Add service", onSelect: () => on.act("addService") },
          // Combine is offered only when there is something to join it with that day.
          ...(on.canCombine ? [{ label: "Combine", onSelect: () => on.act("combine") }] : []),
          { label: "Cancel", onSelect: () => on.act("cancel"), danger: true },
        ]
      : []),
  ];
}

/** The few fields that decide whether two bookings could be joined into one visit (the database's rule, 0051, plus "the same day"). */
export interface CombinableInfo {
  id: string;
  status: string;
  customer_id: string | null;
  /** null = the account holder's own visit; a name = a visit for someone else. Only visits for the same person combine. */
  guest_name: string | null;
  resource_id: string | null;
  starts_at: string | null;
  ends_at: string | null;
}

/** True when another upcoming confirmed booking for the same person (same account holder AND same guest name), with the same staff member, is on the same day (at the business). */
export function hasCombinableOnSameDay(booking: CombinableInfo, all: CombinableInfo[], now: number = Date.now()): boolean {
  if (booking.status !== "confirmed" || !booking.customer_id || !booking.resource_id || !booking.starts_at) return false;
  const day = ymdOfInstant(booking.starts_at);
  return all.some(
    (o) =>
      o.id !== booking.id &&
      o.status === "confirmed" &&
      o.customer_id === booking.customer_id &&
      (o.guest_name ?? null) === (booking.guest_name ?? null) &&
      o.resource_id === booking.resource_id &&
      !!o.starts_at &&
      !!o.ends_at &&
      Date.parse(o.ends_at) > now &&
      ymdOfInstant(o.starts_at) === day
  );
}

const combinableInfo = (r: BookingRow): CombinableInfo => ({
  id: r.id,
  status: r.status,
  customer_id: r.customer_id,
  guest_name: r.guest_name,
  resource_id: r.resource_id,
  starts_at: r.items[0]?.starts_at ?? null,
  ends_at: r.items[r.items.length - 1]?.ends_at ?? null,
});

interface ActionLookups {
  booking: BookingRow;
  /** The same customer's bookings (what Combine can join). */
  rows: BookingRow[];
  services: ServiceOption[];
  resources: ResourceOption[];
  rules: ServiceResourceRule[];
  windows: AvailabilityWindowRow[];
  exceptions: AvailabilityExceptionRow[];
}

/**
 * Reschedule, Add service, Combine and Cancel for ONE booking, as dialogs. It loads the booking and what each dialog needs itself, so the
 * Bookings list and the Dashboard both open them the same way from the quick-action menu. `onChanged` runs after a change so the page can
 * refresh.
 */
export function BookingActionHost({
  businessId,
  bookingId,
  action,
  onClose,
  onChanged,
}: {
  businessId: string;
  bookingId: string;
  action: BookingAction;
  onClose: () => void;
  onChanged: () => Promise<void> | void;
}) {
  const [data, setData] = useState<ActionLookups | null>(null);

  useEffect(() => {
    let cancelled = false;
    setData(null);
    void (async () => {
      const { data: found } = await supabase.from("bookings").select(BOOKING_ROW_SELECT).eq("business_id", businessId).eq("id", bookingId).maybeSingle();
      if (cancelled) return;
      if (!found) return onClose();
      const booking = toBookingRow(found);
      const exceptionsFrom = new Date(Date.now() - 7 * 86_400_000).toISOString();
      const [sameCustomer, svcs, res, srr, avail, exc] = await Promise.all([
        booking.customer_id
          ? supabase.from("bookings").select(BOOKING_ROW_SELECT).eq("business_id", businessId).eq("customer_id", booking.customer_id).eq("status", "confirmed")
          : Promise.resolve({ data: [] as unknown[] }),
        supabase.from("services").select("id, name, category, duration_minutes, intake_fields").eq("business_id", businessId).eq("active", true).order("name"),
        supabase.from("resources").select("id, name, resource_type_id").eq("business_id", businessId).eq("active", true).order("name"),
        supabase.from("service_resource_rules").select("service_id, resource_type_id, resource_id").eq("business_id", businessId),
        supabase.from("availability").select("resource_id, weekday, start_time, end_time").eq("business_id", businessId),
        supabase.from("availability_exceptions").select("resource_id, starts_at, ends_at, kind").eq("business_id", businessId).gte("ends_at", exceptionsFrom),
      ]);
      if (cancelled) return;
      setData({
        booking,
        rows: ((sameCustomer.data ?? []) as any[]).map(toBookingRow),
        services: groupByCategory((svcs.data ?? []) as ServiceOption[]).flatMap((g) => g.items),
        resources: (res.data ?? []) as ResourceOption[],
        rules: (srr.data ?? []) as ServiceResourceRule[],
        windows: (avail.data ?? []) as AvailabilityWindowRow[],
        exceptions: (exc.data ?? []) as AvailabilityExceptionRow[],
      });
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [businessId, bookingId, action]);

  if (!data) return null;
  const done = async () => {
    onClose();
    await onChanged();
  };

  if (action === "cancel") return <CancelBookingModal booking={data.booking} onClose={onClose} onDone={done} />;
  if (action === "combine") return <CombineModal booking={data.booking} rows={data.rows} onClose={onClose} onDone={done} />;
  if (action === "addService") {
    return <AddServiceModal booking={data.booking} services={data.services} resources={data.resources} rules={data.rules} onClose={onClose} onDone={done} />;
  }
  return (
    <RescheduleModal
      booking={data.booking}
      businessId={businessId}
      resources={data.resources}
      services={data.services}
      rules={data.rules}
      windows={data.windows}
      exceptions={data.exceptions}
      onClose={onClose}
      onDone={done}
    />
  );
}

function CancelBookingModal({ booking, onClose, onDone }: { booking: BookingRow; onClose: () => void; onDone: () => Promise<void> }) {
  const [cancelling, setCancelling] = useState(false);
  // "" = the whole booking; otherwise only that service of the visit is cancelled and the rest stays booked.
  const [serviceId, setServiceId] = useState("");
  const [error, setError] = useState<string | null>(null);

  async function handleCancel() {
    setCancelling(true);
    setError(null);
    const { error: cancelErr } = serviceId
      ? await supabase.rpc("rpc_portal_remove_service_from_booking", { p_booking_id: booking.id, p_service_id: serviceId })
      : await supabase.rpc("rpc_portal_cancel_booking", { p_booking_id: booking.id });
    setCancelling(false);
    if (cancelErr) {
      setError(cancelErr.message);
      return;
    }
    await onDone();
  }

  return (
    <Modal title="Cancel booking" onClose={onClose} width={380}>
      <p style={{ fontSize: 13.5, color: "var(--rc-text)", marginTop: 0 }}>
        Cancel {booking.customer_name ?? "this customer"}'s {bookingServiceLabel(booking)}
        {bookingStartsAt(booking) ? ` on ${formatWhen(bookingStartsAt(booking)!)}` : ""}? This frees the slot(s) for other bookings.
      </p>
      {booking.items.length > 1 && (
        <Field label="What to cancel">
          <Select value={serviceId} onChange={(e) => setServiceId(e.target.value)}>
            <option value="">The whole booking</option>
            {booking.items.map((i) => (
              <option key={i.id} value={i.service_id}>
                Only {i.service_name ?? "this service"} (the rest stays booked)
              </option>
            ))}
          </Select>
        </Field>
      )}
      {error && <p style={{ color: "var(--rc-danger)", fontSize: 12.5 }}>{error}</p>}
      <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 16 }}>
        <Button variant="secondary" onClick={onClose}>
          Keep booking
        </Button>
        <Button variant="danger" onClick={handleCancel} disabled={cancelling}>
          {cancelling ? "Cancelling..." : serviceId ? "Cancel service" : "Cancel booking"}
        </Button>
      </div>
    </Modal>
  );
}

function ListView({
  rows,
  resources,
  search,
  setSearch,
  statusFilter,
  setStatusFilter,
  staffFilter,
  setStaffFilter,
  page,
  setPage,
  onComplete,
  onCompleteMany,
  onAction,
  onView,
}: {
  rows: BookingRow[];
  resources: ResourceOption[];
  search: string;
  setSearch: (v: string) => void;
  statusFilter: string;
  setStatusFilter: (v: string) => void;
  staffFilter: string;
  setStaffFilter: (v: string) => void;
  page: number;
  setPage: (v: number) => void;
  onComplete: (r: BookingRow) => void;
  onCompleteMany: (rs: BookingRow[]) => void;
  onAction: (r: BookingRow, action: BookingAction) => void;
  onView: (r: BookingRow) => void;
}) {
  // Bookings ticked for "Mark completed". Only a confirmed booking whose visit has started can be ticked.
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows.filter((r) => {
      if (statusFilter && r.status !== statusFilter) return false;
      if (staffFilter && r.resource_id !== staffFilter) return false;
      if (q && !(r.customer_name ?? "").toLowerCase().includes(q) && !(r.booked_by ?? "").toLowerCase().includes(q) && !bookingServiceLabel(r).toLowerCase().includes(q)) return false;
      return true;
    });
  }, [rows, search, statusFilter, staffFilter]);

  const readOnly = useReadOnly();
  const canComplete = (r: BookingRow) => !readOnly && canMarkCompleted(r.status, bookingStartsAt(r));
  // After a refresh (or a filter change) drop anything no longer ticked-able: completed or cancelled meanwhile, or filtered out.
  useEffect(() => {
    const ok = new Set(filtered.filter(canComplete).map((r) => r.id));
    setSelected((prev) => (prev.size === 0 || [...prev].every((id) => ok.has(id)) ? prev : new Set([...prev].filter((id) => ok.has(id)))));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtered]);
  const selectedRows = filtered.filter((r) => selected.has(r.id));

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const clampedPage = Math.min(page, totalPages - 1);
  const pageRows = filtered.slice(clampedPage * PAGE_SIZE, clampedPage * PAGE_SIZE + PAGE_SIZE);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  // Judged against every loaded booking, not just this page of the list.
  const everyBooking = useMemo(() => rows.map(combinableInfo), [rows]);
  const rowActions = (r: BookingRow): QuickAction[] =>
    bookingQuickActions(r.status, { view: () => onView(r), act: (a) => onAction(r, a), canCombine: hasCombinableOnSameDay(combinableInfo(r), everyBooking), readOnly });
  const pageTickable = pageRows.filter(canComplete);
  const allPageTicked = pageTickable.length > 0 && pageTickable.every((r) => selected.has(r.id));
  const somePageTicked = pageTickable.some((r) => selected.has(r.id));
  function toggleOne(id: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }
  function togglePage() {
    setSelected((prev) => {
      const next = new Set(prev);
      for (const r of pageTickable) {
        if (allPageTicked) next.delete(r.id);
        else next.add(r.id);
      }
      return next;
    });
  }

  return (
    <div>
      <Card style={{ marginBottom: 14, padding: 14 }}>
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
          <div style={{ position: "relative", flex: "1 1 240px", maxWidth: 240 }}>
            <Input
              placeholder="Search customer or service..."
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(0);
              }}
              style={{ paddingRight: search ? 36 : undefined }}
            />
            {search && (
              <button
                type="button"
                aria-label="Clear search"
                onClick={(e) => {
                  setSearch("");
                  setPage(0);
                  (e.currentTarget.parentElement?.querySelector("input") as HTMLInputElement | null)?.focus();
                }}
                style={{
                  position: "absolute",
                  right: 6,
                  top: "50%",
                  transform: "translateY(-50%)",
                  width: 28,
                  height: 28,
                  minHeight: 0,
                  padding: 0,
                  border: "none",
                  borderRadius: 14,
                  background: "none",
                  color: "var(--rc-text-muted)",
                  fontSize: 20,
                  lineHeight: 1,
                  cursor: "pointer",
                }}
              >
                &times;
              </button>
            )}
          </div>
          <Select
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value);
              setPage(0);
            }}
            style={{ width: 160 }}
          >
            <option value="">All statuses</option>
            <option value="confirmed">Confirmed</option>
            <option value="cancelled">Cancelled</option>
            <option value="completed">Completed</option>
            <option value="no_show">No-show</option>
          </Select>
          <Select
            value={staffFilter}
            onChange={(e) => {
              setStaffFilter(e.target.value);
              setPage(0);
            }}
            style={{ width: 180 }}
          >
            <option value="">All staff</option>
            {resources.map((r) => (
              <option key={r.id} value={r.id}>
                {r.name}
              </option>
            ))}
          </Select>
        </div>
      </Card>

      {selectedRows.length > 0 && (
        <Card style={{ marginBottom: 14, padding: "10px 14px", display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
          <span style={{ fontSize: 13.5, fontWeight: 600, color: "var(--rc-text)" }}>{selectedRows.length} selected</span>
          <Button variant="primary" onClick={() => onCompleteMany(selectedRows)}>
            Mark completed ({selectedRows.length})
          </Button>
          <Button variant="ghost" onClick={() => setSelected(new Set())}>
            Clear
          </Button>
        </Card>
      )}

      <Card padded={false}>
        <div className="rc-table-scroll rc-sticky-head"><table className="rc-stack" style={{ width: "100%", borderCollapse: "collapse", fontSize: 14 }}>
          <thead>
            <tr style={{ textAlign: "left", borderBottom: "1px solid var(--rc-border)", color: "var(--rc-text-muted)" }}>
              <th style={{ padding: "10px 0 10px 12px", width: 28 }}>
                {!readOnly && <input
                  type="checkbox"
                  aria-label="Select all completable bookings on this page"
                  checked={allPageTicked}
                  ref={(el) => {
                    if (el) el.indeterminate = somePageTicked && !allPageTicked;
                  }}
                  disabled={pageTickable.length === 0}
                  onChange={togglePage}
                />}
              </th>
              {["When", "With", "Customer", "Service", "Status", ""].map((h) => (
                <th key={h} style={{ padding: "10px 12px" }}>
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {pageRows.map((r) => {
              const hasDetails = r.items.some((i) => i.custom_fields && Object.keys(i.custom_fields).length > 0);
              const startsAt = bookingStartsAt(r);
              return (
                <Fragment key={r.id}>
                  <tr style={{ borderBottom: "1px solid var(--rc-border-soft)" }}>
                    <td data-label="Select" className={canComplete(r) ? undefined : "rc-skip"} style={{ padding: "10px 0 10px 12px", width: 28 }}>
                      {canComplete(r) ? (
                        <input type="checkbox" aria-label={`Select ${r.customer_name ?? "booking"}`} checked={selected.has(r.id)} onChange={() => toggleOne(r.id)} />
                      ) : null}
                    </td>
                    <td data-label="When" style={{ padding: "10px 12px", fontWeight: 700 }}>
                      {startsAt ? formatWhen(startsAt) : "-"}
                    </td>
                    <td data-label="With" style={{ padding: "10px 12px", fontWeight: 700 }}>{r.resource_name ?? "-"}</td>
                    <td data-label="Customer" style={{ padding: "10px 12px", fontWeight: 600 }}>{r.customer_name ?? "-"}{r.booked_by && <div style={{ fontSize: 12, fontWeight: 400, color: "var(--rc-text-muted)" }}>booked by {r.booked_by}</div>}</td>
                    <td data-label="Service" style={{ padding: "10px 12px" }}>{bookingServiceLabel(r)}</td>
                    <td data-label="Status" style={{ padding: "10px 12px" }}>
                      <Badge tone={STATUS_TONE[r.status] ?? "neutral"}>{r.status.replace("_", " ")}</Badge>
                    </td>
                    <td className="rc-actions" style={{ padding: "10px 12px", whiteSpace: "nowrap", textAlign: "right" }}>
                      {r.status === "confirmed" && canComplete(r) && (
                        <button onClick={() => onComplete(r)} style={{ ...linkBtn, marginRight: 4 }}>
                          Mark completed
                        </button>
                      )}
                      {hasDetails && (
                        <button onClick={() => setExpandedId((v) => (v === r.id ? null : r.id))} style={{ ...linkBtn, marginRight: 4 }}>
                          {expandedId === r.id ? "Hide" : "Details"}
                        </button>
                      )}
                      <QuickActions actions={rowActions(r)} label={`Actions for ${r.customer_name ?? "booking"}`} />
                    </td>
                  </tr>
                  {expandedId === r.id && hasDetails && (
                    <tr style={{ background: "var(--rc-surface-sunken)" }}>
                      <td colSpan={7} style={{ padding: "10px 14px", fontSize: 12.5, color: "var(--rc-text-muted)" }}>
                        {r.items.map((item) => {
                          const entries = Object.entries(item.custom_fields ?? {}).filter(
                            ([, v]) => v !== "" && v !== false && !(Array.isArray(v) && v.length === 0)
                          );
                          if (entries.length === 0) return null;
                          return (
                            <div key={item.id} style={{ marginBottom: 6 }}>
                              {r.items.length > 1 && <div style={{ fontWeight: 700, color: "var(--rc-text)" }}>{item.service_name}</div>}
                              {entries.map(([k, v]) => (
                                <div key={k}>
                                  <strong style={{ color: "var(--rc-text)" }}>{k}:</strong> {Array.isArray(v) ? v.join(", ") : String(v)}
                                </div>
                              ))}
                            </div>
                          );
                        })}
                      </td>
                    </tr>
                  )}
                </Fragment>
              );
            })}
            {pageRows.length === 0 && (
              <tr>
                <td colSpan={7}>
                  <EmptyState>No bookings match these filters.</EmptyState>
                </td>
              </tr>
            )}
          </tbody>
        </table></div>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px 14px", borderTop: "1px solid var(--rc-border)" }}>
          <span style={{ fontSize: 12, color: "var(--rc-text-faint)" }}>
            {filtered.length} booking{filtered.length === 1 ? "" : "s"}
          </span>
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <Button variant="ghost" disabled={clampedPage === 0} onClick={() => setPage(clampedPage - 1)}>
              Prev
            </Button>
            <span style={{ fontSize: 12, color: "var(--rc-text-muted)" }}>
              Page {clampedPage + 1} of {totalPages}
            </span>
            <Button variant="ghost" disabled={clampedPage >= totalPages - 1} onClick={() => setPage(clampedPage + 1)}>
              Next
            </Button>
          </div>
        </div>
      </Card>
    </div>
  );
}

const linkBtn: React.CSSProperties = {
  background: "none",
  border: "none",
  color: "var(--rc-link)",
  cursor: "pointer",
  padding: "2px 8px",
  fontSize: 12.5,
  fontWeight: 600,
};

function RescheduleModal({
  booking,
  businessId,
  resources,
  services,
  rules,
  windows,
  exceptions,
  onClose,
  onDone,
}: {
  booking: BookingRow;
  businessId: string;
  resources: ResourceOption[];
  services: ServiceOption[];
  rules: ServiceResourceRule[];
  windows: AvailabilityWindowRow[];
  exceptions: AvailabilityExceptionRow[];
  onClose: () => void;
  onDone: () => Promise<void>;
}) {
  const [date, setDate] = useState(() => {
    const startsAt = bookingStartsAt(booking);
    const original = startsAt ? ymdOfInstant(startsAt) : undefined; // the business-local day (the first 10 characters would be the UTC day)
    const today = todayStr();
    // A booking already in the past can't be rescheduled *to* its own old date -- start the
    // picker at today instead so it doesn't open on an all-past, nothing-clickable day.
    return original && original > today ? original : today;
  });
  const [busy, setBusy] = useState<BusyItem[]>([]);
  const [selected, setSelected] = useState<{ resourceId: string; startMin: number } | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const serviceIds = useMemo(() => booking.items.map((i) => i.service_id), [booking]);
  const bookingServices = useMemo(
    () => serviceIds.map((id) => services.find((s) => s.id === id)).filter((s): s is ServiceOption => !!s),
    [serviceIds, services]
  );
  const totalDurationMin = useMemo(() => bookingServices.reduce((sum, s) => sum + s.duration_minutes, 0), [bookingServices]);
  const eligible: GridResource[] = useMemo(() => eligibleForAll(serviceIds, resources, rules).sort(bySurname), [serviceIds, resources, rules]);

  useEffect(() => {
    void (async () => {
      const dayStart = startOfDayInZone(date).toISOString();
      const dayEnd = new Date(endOfDayExclusive(date).getTime() - 1).toISOString();
      const { data: items } = await supabase
        .from("booking_items")
        .select("booking_id, resource_id, starts_at, ends_at, services(name), bookings!inner(status, guest_name, customers(name))")
        .eq("business_id", businessId)
        .is("cancelled_at", null)
        .lt("starts_at", dayEnd)
        .gt("ends_at", dayStart);
      // Excludes this booking's OWN items -- rescheduling moves them, so overlapping with their
      // current (about-to-move) time isn't a real conflict, just this booking's own old slot.
      const list: BusyItem[] = (items ?? [])
        .filter((i: any) => i.bookings?.status === "confirmed" && i.booking_id !== booking.id)
        .map((i: any) => ({
          resource_id: i.resource_id,
          starts_at: i.starts_at,
          ends_at: i.ends_at,
          kind: "booking" as const,
          label: `${i.bookings?.guest_name ?? i.bookings?.customers?.name ?? "Customer"} · ${i.services?.name ?? "Booking"}`,
        }));
      setBusy(list);
    })();
  }, [date]);

  async function confirmReschedule() {
    if (!selected || bookingServices.length === 0) return;
    setSaving(true);
    setError(null);
    const blockStart = zonedWallToInstant(date, wallTimeOfMinutes(selected.startMin));

    // Moves every service of the visit together, atomically, under the same rules as the phone.
    const { error: moveErr } = await supabase.rpc("rpc_portal_reschedule_booking", {
      p_booking_id: booking.id,
      p_starts_at: blockStart.toISOString(),
      p_resource_id: selected.resourceId,
    });
    if (moveErr) {
      setSaving(false);
      setError(bookingErrorMessage(moveErr));
      return;
    }

    setSaving(false);
    await onDone();
  }

  const originalStartsAt = bookingStartsAt(booking);
  const originalLabel = originalStartsAt
    ? formatWhen(originalStartsAt)
    : null;

  return (
    <Modal
      title={
        <>
          Reschedule {booking.customer_name ?? "booking"}
          {originalLabel && (
            <span style={{ fontSize: 12.5, fontWeight: 500, color: "var(--rc-text-faint)", marginLeft: 8 }}>
              was {originalLabel} · {bookingServiceLabel(booking)} ({totalDurationMin} min)
            </span>
          )}
        </>
      }
      onClose={onClose}
      width={720}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 4 }}>
        <Button variant="ghost" disabled={date <= todayStr()} onClick={() => setDate(shiftDate(date, -1))}>
          ← Prev
        </Button>
        <Input type="date" min={todayStr()} value={date} onChange={(e) => setDate(e.target.value)} style={{ width: 160 }} />
        <Button variant="ghost" onClick={() => setDate(shiftDate(date, 1))}>
          Next →
        </Button>
        <div style={{ flex: 1 }} />
        <Button variant="secondary" onClick={onClose}>
          Cancel
        </Button>
        <Button variant="primary" disabled={!selected || saving} onClick={confirmReschedule}>
          {saving ? "Saving..." : "Confirm new time"}
        </Button>
      </div>
      {error && <p style={{ color: "var(--rc-danger)", fontSize: 12.5, margin: "0 0 8px" }}>{error}</p>}
      <StaffAvailabilityGrid
        date={date}
        resources={eligible}
        windows={windows}
        exceptions={exceptions}
        busy={busy}
        slotDurationMin={totalDurationMin > 0 ? totalDurationMin : null}
        selected={selected}
        onSelectSlot={(resourceId, startMin) => setSelected({ resourceId, startMin })}
      />
    </Modal>
  );
}

interface AddServicePreview {
  placement: "after" | "before";
  session_starts_at: string;
  session_ends_at: string;
}

const timeLabel = (iso: string) => formatTime(iso);

/** Adds a service to an existing booking so it stays ONE visit (services back to back) -- the same
 *  rule the phone uses: straight after the booking if that time is free, otherwise straight before
 *  it (the visit then starts earlier, shown before confirming). Nothing else is ever moved. */
function AddServiceModal({
  booking,
  services,
  resources,
  rules,
  onClose,
  onDone,
}: {
  booking: BookingRow;
  services: ServiceOption[];
  resources: ResourceOption[];
  rules: ServiceResourceRule[];
  onClose: () => void;
  onDone: () => Promise<void>;
}) {
  const [serviceId, setServiceId] = useState("");
  const [preview, setPreview] = useState<AddServicePreview | null>(null);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [checking, setChecking] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Only services this booking's staff member is set up to do (the database checks this too).
  const options = useMemo(() => {
    const staff = resources.filter((r) => r.id === booking.resource_id);
    return services.filter((svc) => eligibleForAll([svc.id], staff, rules).length > 0);
  }, [booking.resource_id, services, resources, rules]);

  useEffect(() => {
    setPreview(null);
    setPreviewError(null);
    if (!serviceId) return;
    let cancelled = false;
    setChecking(true);
    void (async () => {
      for (const placement of ["after", "before"] as const) {
        const { data, error: dryErr } = await supabase.rpc("rpc_portal_add_service_to_booking", {
          p_booking_id: booking.id,
          p_service_id: serviceId,
          p_placement: placement,
          p_dry_run: true,
        });
        if (cancelled) return;
        if (!dryErr) {
          setPreview({ placement, session_starts_at: data.session_starts_at, session_ends_at: data.session_ends_at });
          setChecking(false);
          return;
        }
        if (!/slot_unavailable|outside_business_hours/.test(dryErr.message ?? "")) {
          setPreviewError(bookingErrorMessage(dryErr));
          setChecking(false);
          return;
        }
      }
      setPreviewError("There isn't free time right next to this booking to fit that in the same visit.");
      setChecking(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [serviceId, booking.id]);

  async function confirm() {
    if (!preview || !serviceId) return;
    setSaving(true);
    setError(null);
    const { error: addErr } = await supabase.rpc("rpc_portal_add_service_to_booking", {
      p_booking_id: booking.id,
      p_service_id: serviceId,
      p_placement: preview.placement,
      p_dry_run: false,
    });
    setSaving(false);
    if (addErr) {
      setError(bookingErrorMessage(addErr));
      return;
    }
    await onDone();
  }

  const originalStart = bookingStartsAt(booking);
  return (
    <Modal title={`Add a service for ${booking.customer_name ?? "this booking"}`} onClose={onClose} width={440}>
      <p style={{ fontSize: 13, color: "var(--rc-text-muted)", marginTop: 0 }}>
        Currently {bookingServiceLabel(booking)}
        {originalStart ? ` on ${formatWhen(originalStart)}` : ""}. The new
        service joins the same visit, back to back with the same staff member.
      </p>
      <Field label="Service to add">
        <Select value={serviceId} onChange={(e) => setServiceId(e.target.value)}>
          <option value="">Select a service...</option>
          {options.map((svc) => (
            <option key={svc.id} value={svc.id}>
              {svc.name} ({svc.duration_minutes} min)
            </option>
          ))}
        </Select>
      </Field>
      {checking && <p style={{ fontSize: 12.5, color: "var(--rc-text-faint)" }}>Checking the schedule...</p>}
      {preview && (
        <p style={{ fontSize: 12.5, color: "var(--rc-success)" }}>
          {preview.placement === "after"
            ? `It fits straight after the booking. The visit will run ${timeLabel(preview.session_starts_at)} to ${timeLabel(preview.session_ends_at)}.`
            : `Nothing is free straight after, but it fits straight before. The visit will start earlier: ${timeLabel(preview.session_starts_at)} to ${timeLabel(preview.session_ends_at)}.`}
        </p>
      )}
      {previewError && <p style={{ fontSize: 12.5, color: "var(--rc-danger)" }}>{previewError}</p>}
      {error && <p style={{ fontSize: 12.5, color: "var(--rc-danger)" }}>{error}</p>}
      <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 16 }}>
        <Button variant="secondary" onClick={onClose}>
          Cancel
        </Button>
        <Button variant="primary" onClick={confirm} disabled={!preview || saving}>
          {saving ? "Adding..." : "Add to this visit"}
        </Button>
      </div>
    </Modal>
  );
}

/** Joins separate bookings of the same customer (same staff member) into ONE visit, the same rule the phone uses:
 *  services back to back from the earliest booking's start (or ending at the last one's end if that is the only fit),
 *  nothing else moved. A dry run shows the result before anything changes. */
function CombineModal({
  booking,
  rows,
  onClose,
  onDone,
}: {
  booking: BookingRow;
  rows: BookingRow[];
  onClose: () => void;
  onDone: () => Promise<void>;
}) {
  const [picked, setPicked] = useState<string[]>([]);
  const [preview, setPreview] = useState<{ session_starts_at: string; session_ends_at: string } | null>(null);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const now = new Date().toISOString();
  const candidates = rows.filter(
    (r) =>
      r.id !== booking.id &&
      r.status === "confirmed" &&
      r.customer_id != null &&
      r.customer_id === booking.customer_id &&
      r.guest_name === booking.guest_name && // a visit for Alice is never joined to the account holder's own visit
      r.resource_id === booking.resource_id &&
      (r.items[r.items.length - 1]?.ends_at ?? "") > now
  );

  useEffect(() => {
    setPreview(null);
    setPreviewError(null);
    if (picked.length === 0) return;
    let cancelled = false;
    void supabase.rpc("rpc_portal_combine_bookings", { p_booking_ids: [booking.id, ...picked], p_dry_run: true }).then(({ data, error: dryErr }) => {
      if (cancelled) return;
      if (dryErr) {
        setPreviewError(
          /slot_unavailable/.test(dryErr.message ?? "")
            ? "There isn't free time to put these back to back, from the first booking's start or ending at the last one's end."
            : bookingErrorMessage(dryErr)
        );
      } else {
        setPreview({ session_starts_at: data.session_starts_at, session_ends_at: data.session_ends_at });
      }
    });
    return () => {
      cancelled = true;
    };
  }, [picked, booking.id]);

  async function confirm() {
    setSaving(true);
    setError(null);
    const { error: err } = await supabase.rpc("rpc_portal_combine_bookings", { p_booking_ids: [booking.id, ...picked], p_dry_run: false });
    setSaving(false);
    if (err) {
      setError(bookingErrorMessage(err));
      return;
    }
    await onDone();
  }

  const startLabel = bookingStartsAt(booking);
  return (
    <Modal title={`Combine bookings for ${booking.customer_name ?? "this customer"}`} onClose={onClose} width={480}>
      <p style={{ fontSize: 13, color: "var(--rc-text-muted)", marginTop: 0 }}>
        {bookingServiceLabel(booking)}
        {startLabel ? ` on ${formatWhen(startLabel)}` : ""}. Pick the other bookings to join into
        one visit with the same staff member. They run back to back from the earliest start.
      </p>
      {candidates.length === 0 ? (
        <p style={{ fontSize: 13, color: "var(--rc-text-faint)" }}>No other upcoming bookings for this customer with the same staff member.</p>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          {candidates.map((c) => (
            <label key={c.id} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13.5 }}>
              <input
                type="checkbox"
                checked={picked.includes(c.id)}
                onChange={() => setPicked((prev) => (prev.includes(c.id) ? prev.filter((x) => x !== c.id) : [...prev, c.id]))}
              />
              {bookingServiceLabel(c)}
              {bookingStartsAt(c) ? `, ${formatWhen(bookingStartsAt(c)!)}` : ""}
            </label>
          ))}
        </div>
      )}
      {preview && (
        <p style={{ fontSize: 12.5, color: "var(--rc-success)" }}>
          One visit, {timeLabel(preview.session_starts_at)} to {timeLabel(preview.session_ends_at)}.
        </p>
      )}
      {previewError && <p style={{ fontSize: 12.5, color: "var(--rc-danger)" }}>{previewError}</p>}
      {error && <p style={{ fontSize: 12.5, color: "var(--rc-danger)" }}>{error}</p>}
      <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 16 }}>
        <Button variant="secondary" onClick={onClose}>
          Cancel
        </Button>
        <Button variant="primary" onClick={confirm} disabled={!preview || saving}>
          {saving ? "Combining..." : "Combine into one visit"}
        </Button>
      </div>
    </Modal>
  );
}
